
import { supabase } from '@/integrations/supabase/client';
import { AdminApiClient, AdminAuthState, AdminUser, DashboardStats } from '../types/admin-api-types';
import { Trek, Company, TrekPackage } from '@/lib/types/trek-types';
import { ContactInquiry, InquiryStatus } from '@/lib/types/contact-types';
import { 
  mapDbSchemaToTrek, 
  mapTrekToDbSchema,
  mapDbSchemaToCompany,
  mapCompanyToDbSchema,
  mapDbSchemaToPackage,
  mapPackageToDbSchema,
  mapDbSchemaToInquiry,
  mapDbSchemaToAdmin
} from '@/lib/mappers';

export class SupabaseAdminApiClient implements AdminApiClient {
  // Admin users management methods
  async getAdmins(): Promise<{ data: AdminUser[]; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('admin_users')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      return { data: data.map(admin => mapDbSchemaToAdmin(admin)) };
    } catch (error) {
      console.error('Error fetching admins:', error);
      return { data: [], error: error as Error };
    }
  }
  
  async addAdmin(email: string): Promise<{ data: AdminUser | null; error?: Error }> {
    try {
      // First check if user already exists in admin_users table
      const { data: existingAdmin } = await supabase
        .from('admin_users')
        .select('*')
        .eq('email', email)
        .single();
        
      if (existingAdmin) {
        return { 
          data: null, 
          error: new Error(`${email} is already an admin.`) 
        };
      }
      
      // In a real application with auth, we would verify the user exists in auth.users
      // Since we don't have direct access to auth.users in this example, we'll create a new admin
      const { data, error } = await supabase
        .from('admin_users')
        .insert({
          id: crypto.randomUUID(),
          email: email,
          role: 'admin'
        })
        .select();
        
      if (error) throw error;
      
      return { data: data && data.length > 0 ? mapDbSchemaToAdmin(data[0]) : null };
    } catch (error) {
      console.error('Error adding admin:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async deleteAdmin(id: string): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase
        .from('admin_users')
        .delete()
        .eq('id', id);

      if (error) throw error;
      
      return {};
    } catch (error) {
      console.error('Error deleting admin:', error);
      return { error: error as Error };
    }
  }
  
  // Dashboard statistics methods
  async getDashboardStats(): Promise<{ data: DashboardStats; error?: Error }> {
    try {
      // Get treks count
      const { count: treksCount, error: treksError } = await supabase
        .from('treks')
        .select('*', { count: 'exact', head: true });
      
      if (treksError) throw treksError;
      
      // Get companies count
      const { count: companiesCount, error: companiesError } = await supabase
        .from('companies')
        .select('*', { count: 'exact', head: true });
      
      if (companiesError) throw companiesError;
      
      // Get packages count
      const { count: packagesCount, error: packagesError } = await supabase
        .from('trek_packages')
        .select('*', { count: 'exact', head: true });
      
      if (packagesError) throw packagesError;
      
      return {
        data: {
          treksCount: treksCount || 0,
          companiesCount: companiesCount || 0,
          packagesCount: packagesCount || 0
        }
      };
    } catch (error) {
      console.error('Error fetching dashboard stats:', error);
      return { 
        data: { treksCount: 0, companiesCount: 0, packagesCount: 0 }, 
        error: error as Error 
      };
    }
  }

  // Authentication methods
  async login(email: string, password: string): Promise<{ data: any; error?: Error }> {
    try {
      const { data, error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      
      if (error) throw error;
      
      return { data };
    } catch (error) {
      console.error('Login error:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async logout(): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      return {};
    } catch (error) {
      console.error('Logout error:', error);
      return { error: error as Error };
    }
  }
  
  async isAdmin(userId: string): Promise<boolean> {
    try {
      const { data, error } = await supabase.rpc('is_admin', { user_uid: userId });
      if (error) throw error;
      return !!data;
    } catch (error) {
      console.error('Admin check error:', error);
      return false;
    }
  }
  
  async getCurrentUser(): Promise<{ user: any | null; error?: Error }> {
    try {
      const { data, error } = await supabase.auth.getUser();
      if (error) throw error;
      return { user: data.user };
    } catch (error) {
      console.error('Get current user error:', error);
      return { user: null, error: error as Error };
    }
  }
  
  async getAuthState(): Promise<AdminAuthState> {
    try {
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError) throw sessionError;
      
      if (!sessionData.session) {
        return {
          isAuthenticated: false,
          isLoading: false,
        };
      }
      
      const userId = sessionData.session.user.id;
      const isAdminCheck = await this.isAdmin(userId);
      
      return {
        isAuthenticated: isAdminCheck,
        isLoading: false,
        userId,
        email: sessionData.session.user.email,
      };
    } catch (error) {
      console.error('Get auth state error:', error);
      return {
        isAuthenticated: false,
        isLoading: false,
        error: error as Error,
      };
    }
  }
  
  // Trek management methods
  async getAllTreks(): Promise<{ data: Trek[]; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('treks')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;

      return { data: data.map(trek => mapDbSchemaToTrek(trek)) };
    } catch (error) {
      console.error('Error fetching all treks:', error);
      return { data: [], error: error as Error };
    }
  }

  async getTrekById(id: string): Promise<{ data: Trek | null; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('treks')
        .select('*')
        .eq('id', id)
        .maybeSingle();
        
      if (error) throw error;
      
      return { data: data ? mapDbSchemaToTrek(data) : null };
    } catch (error) {
      console.error('Error fetching trek by ID:', error);
      return { data: null, error: error as Error };
    }
  }

  async createTrek(trekData: Partial<Trek>): Promise<{ data: Trek | null; error?: Error }> {
    try {
      const dbTrekData = mapTrekToDbSchema(trekData);
      const { data, error } = await supabase
        .from('treks')
        .insert(dbTrekData)
        .select('*')
        .single();
        
      if (error) throw error;
      return { data: mapDbSchemaToTrek(data) };
    } catch (error) {
      console.error('Create trek error:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async updateTrek(id: string, trekData: Partial<Trek>): Promise<{ data: Trek | null; error?: Error }> {
    try {
      const dbTrekData = mapTrekToDbSchema(trekData);
      const { data, error } = await supabase
        .from('treks')
        .update(dbTrekData)
        .eq('id', id)
        .select('*')
        .single();
        
      if (error) throw error;
      return { data: mapDbSchemaToTrek(data) };
    } catch (error) {
      console.error('Update trek error:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async deleteTrek(id: string): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase
        .from('treks')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return {};
    } catch (error) {
      console.error('Delete trek error:', error);
      return { error: error as Error };
    }
  }
  
  // Company management methods
  async getAllCompanies(): Promise<{ data: Company[]; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;

      return { data: data.map(company => mapDbSchemaToCompany(company)) };
    } catch (error) {
      console.error('Error fetching all companies:', error);
      return { data: [], error: error as Error };
    }
  }

  async getCompanyById(id: string): Promise<{ data: Company | null; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('id', id)
        .maybeSingle();
        
      if (error) throw error;
      
      return { data: data ? mapDbSchemaToCompany(data) : null };
    } catch (error) {
      console.error('Error fetching company by ID:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async createCompany(companyData: Partial<Company>): Promise<{ data: Company | null; error?: Error }> {
    try {
      const dbCompanyData = mapCompanyToDbSchema(companyData);
      const { data, error } = await supabase
        .from('companies')
        .insert(dbCompanyData)
        .select('*')
        .single();
        
      if (error) throw error;
      return { data: mapDbSchemaToCompany(data) };
    } catch (error) {
      console.error('Create company error:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async updateCompany(id: string, companyData: Partial<Company>): Promise<{ data: Company | null; error?: Error }> {
    try {
      const dbCompanyData = mapCompanyToDbSchema(companyData);
      const { data, error } = await supabase
        .from('companies')
        .update(dbCompanyData)
        .eq('id', id)
        .select('*')
        .single();
        
      if (error) throw error;
      return { data: mapDbSchemaToCompany(data) };
    } catch (error) {
      console.error('Update company error:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async deleteCompany(id: string): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase
        .from('companies')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return {};
    } catch (error) {
      console.error('Delete company error:', error);
      return { error: error as Error };
    }
  }
  
  // Package management methods
  async getAllPackages(): Promise<{ data: TrekPackage[]; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('trek_packages')
        .select('*')
        .order('created_at', { ascending: false });
        
      if (error) throw error;

      return { data: data.map(pkg => mapDbSchemaToPackage(pkg)) };
    } catch (error) {
      console.error('Error fetching all packages:', error);
      return { data: [], error: error as Error };
    }
  }

  async getPackageById(id: string): Promise<{ data: TrekPackage | null; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('trek_packages')
        .select('*')
        .eq('id', id)
        .maybeSingle();
        
      if (error) throw error;
      
      return { data: data ? mapDbSchemaToPackage(data) : null };
    } catch (error) {
      console.error('Error fetching package by ID:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async createPackage(packageData: Partial<TrekPackage>): Promise<{ data: TrekPackage | null; error?: Error }> {
    try {
      const dbPackageData = mapPackageToDbSchema(packageData);
      const { data, error } = await supabase
        .from('trek_packages')
        .insert(dbPackageData)
        .select('*')
        .single();
        
      if (error) throw error;
      return { data: mapDbSchemaToPackage(data) };
    } catch (error) {
      console.error('Create package error:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async updatePackage(id: string, packageData: Partial<TrekPackage>): Promise<{ data: TrekPackage | null; error?: Error }> {
    try {
      const dbPackageData = mapPackageToDbSchema(packageData);
      const { data, error } = await supabase
        .from('trek_packages')
        .update(dbPackageData)
        .eq('id', id)
        .select('*')
        .single();
        
      if (error) throw error;
      return { data: mapDbSchemaToPackage(data) };
    } catch (error) {
      console.error('Update package error:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async deletePackage(id: string): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase
        .from('trek_packages')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return {};
    } catch (error) {
      console.error('Delete package error:', error);
      return { error: error as Error };
    }
  }
  
  // Contact inquiries management methods
  async getInquiries(
    page = 1, 
    pageSize = 10, 
    statusFilter: InquiryStatus | 'all' = 'all',
    searchTerm = ''
  ): Promise<{ data: ContactInquiry[]; count: number | null; error?: Error }> {
    try {
      const startIndex = (page - 1) * pageSize;
      const endIndex = startIndex + pageSize - 1;
      
      let query = supabase
        .from('contact_inquiries')
        .select('*', { count: 'exact' });
      
      // Apply status filter if not 'all'
      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      
      // Apply search term if provided
      if (searchTerm) {
        query = query.or(
          `name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%,subject.ilike.%${searchTerm}%,message.ilike.%${searchTerm}%`
        );
      }
      
      // Add pagination and order
      const { data, error, count } = await query
        .order('created_at', { ascending: false })
        .range(startIndex, endIndex);
      
      if (error) throw error;
      
      return { 
        data: data.map(inquiry => mapDbSchemaToInquiry(inquiry)), 
        count, 
        error: undefined 
      };
    } catch (error) {
      console.error('Error fetching contact inquiries:', error);
      return { data: [], count: null, error: error as Error };
    }
  }
  
  async updateInquiryStatus(
    id: string,
    status: InquiryStatus
  ): Promise<{ data: ContactInquiry | null; error?: Error }> {
    try {
      const { data, error } = await supabase
        .from('contact_inquiries')
        .update({ 
          status,
          ...(status === 'resolved' ? { resolved_at: new Date().toISOString() } : {}),
          updated_at: new Date().toISOString()
        })
        .eq('id', id)
        .select('*')
        .single();
      
      if (error) throw error;
      
      return { data: mapDbSchemaToInquiry(data) };
    } catch (error) {
      console.error('Error updating inquiry status:', error);
      return { data: null, error: error as Error };
    }
  }
  
  async deleteInquiry(id: string): Promise<{ error?: Error }> {
    try {
      const { error } = await supabase
        .from('contact_inquiries')
        .delete()
        .eq('id', id);
        
      if (error) throw error;
      return {};
    } catch (error) {
      console.error('Delete inquiry error:', error);
      return { error: error as Error };
    }
  }
}

export const supabaseAdminClient = new SupabaseAdminApiClient();
